// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
// Material
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
// RXJS
import { debounceTime, delay, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge, of, Subscription } from 'rxjs';
// Translate Module
import { TranslateService } from '@ngx-translate/core';
// NGRX
import { select, Store } from '@ngrx/store';
// Services
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
// Models
import {
  AuthService,
  currentUserRoleIds,
  Role,
  RoleActionTypes,
  RoleOnServerDeleted,
  RolesDataSource,
  RolesOnServerDeleted,
  RolesPageRequested,
  selectRoleError,
  selectRoleLatestSuccessfullAction,
} from '../../../../../core/auth';
import { AppState } from '../../../../../core/reducers';
// Components
import { RoleEditDialogComponent } from '../role-edit/role-edit.dialog.component';
// Management components
import { CompaniesService, CompanyModel } from 'src/app/core/management';
import { MatSelect } from '@angular/material/select';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { utc } from 'moment';
import { DownloadService } from '../../../../../shared/utils/download.service';

@Component({
  selector: 'kt-roles-list',
  templateUrl: './roles-list.component.html',
  styleUrls: ['./roles-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolesListComponent implements OnInit, OnDestroy {
  // Table fields
  dataSource: RolesDataSource;
  displayedColumns = [];
  searchBarColumns = [];
  showSearchBar = false;
  departments$ = this.authService.getTwixorDepartments('ALL');
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  // Filter fields
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchTitleInput') searchTitleInput: ElementRef;
  @ViewChild('searchDepartmentSelect') searchDepartmentSelect: MatSelect;
  getCompanyAutocomplete = this.companiesService.searchCompanies.bind(this.companiesService);
  @ViewChild('searchCompanyAutocomplete') searchCompanyAutocomplete: GeneralAutocompleteComponent;
  // Selection
  selection = new SelectionModel<Role>(true, []);
  rolesResult: Role[] = [];
  companies: CompanyModel[] = [];
  companiesNoAdminRole: CompanyModel[] = [];
  isSuperAdmin: boolean;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  /**
   * Component constructor
   *
   * @param store
   * @param dialog
   * @param snackBar
   * @param layoutUtilsService
   * @param companiesService
   * @param cdr
   * @param translate
   * @param authService
   * @param downloadService
   */
  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private authService: AuthService,
    private downloadService: DownloadService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    this.subscriptions.push(
      this.store.pipe(select(currentUserRoleIds)).subscribe((res) => {
        this.isSuperAdmin = res ? res.includes(1) : null;
        this.displayedColumns = this.isSuperAdmin
          ? ['select', 'title', 'template', 'company', 'actions']
          : ['select', 'title', 'template', 'department', 'actions'];
        this.searchBarColumns = this.isSuperAdmin
          ? ['searchSelect', 'searchTitle', 'searchCompany', 'searchActions']
          : ['searchSelect', 'searchTitle', 'searchDepartment', 'searchActions'];
        if (this.isSuperAdmin) {
          this.loadCompaniesList();
        }
      }),
    );
    // If the user changes the sort order, reset back to the first page.
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => {
        this.paginator.pageIndex = 0;
        this.sort.active = this.sort.direction ? this.sort.active : 'id';
        this.sort.direction = this.sort.direction || 'desc';
      }),
    );

    /* Data load will be triggered in two cases:
		- when a pagination event occurs => this.paginator.page
		- when a sort event occurs => this.sort.sortChange
		**/
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            this.loadRolesList();
          }),
        )
        .subscribe(),
    );

    // Filtration, bind to searchInput
    this.subscriptions.push(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          // tslint:disable-next-line:max-line-length
          debounceTime(150), // The user can type quite quickly in the input box, and that could trigger a lot of server requests. With this operator, we are limiting the amount of server requests emitted to a maximum of one every 150ms
          distinctUntilChanged(), // This operator will eliminate duplicate values
          tap(() => {
            this.paginator.pageIndex = 0;
            this.loadRolesList();
          }),
        )
        .subscribe(),
    );

    // Init DataSource
    this.dataSource = new RolesDataSource(this.store);
    this.subscriptions.push(
      this.dataSource.entitySubject.pipe(skip(1), distinctUntilChanged()).subscribe((res) => {
        this.rolesResult = res;
      }),
    );

    // First load
    of(undefined)
      .pipe(take(1), delay(1000))
      .subscribe(() => {
        // Remove this line, just loading imitation
        this.loadRolesList();
      });
    this.subscriptions.push(
      this.store.pipe(select(selectRoleError), skip(1)).subscribe((res) => {
        if (res) {
          this.layoutUtilsService.generalDialog(
            '',
            `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">${
                res.error?.errors[0]?.message || this.translate.instant('GLOBAL.UNSPECIFIED_ERROR')
              }</p>
            </div>
          `,
            '',
            '',
            'Close',
            false,
            true,
            '400px',
          );
        }
      }),
    );
    this.subscriptions.push(
      this.store.pipe(select(selectRoleLatestSuccessfullAction), skip(1)).subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case RoleActionTypes.RoleCreated:
              this.loadRolesList();
              successMsg = this.translate.instant('ROLES.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case RoleActionTypes.RoleUpdated:
              this.loadRolesList();
              successMsg = this.translate.instant('ROLES.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
            case RoleActionTypes.RolesDeleted:
              this.loadRolesList();
              this.selection.clear();
              successMsg = this.translate.instant('ROLES.DELETE_ROLE_MULTI.MESSAGE');
              msgType = MessageType.Delete;
              break;
            case RoleActionTypes.RoleDeleted:
              this.loadRolesList();
              successMsg = this.translate.instant('ROLES.DELETE_ROLE_SIMPLE.MESSAGE');
              msgType = MessageType.Delete;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
          }
        }
      }),
    );
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Load Roles List
   */
  loadRolesList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(new RolesPageRequested({ page: queryParams }));
    this.selection.clear();
  }

  loadCompaniesList() {
    this.companiesService.getAllCompanies().subscribe((res) => (this.companies = res));
    this.companiesService
      .getCompaniesNoAdminRole()
      .subscribe((res) => (this.companiesNoAdminRole = res));
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    const searchText: string = this.searchInput.nativeElement.value;
    filter.title = searchText;
    filter.searchData = searchText;

    filter.searchTitle = this.searchTitleInput?.nativeElement?.value;
    filter.searchDepartment = this.searchDepartmentSelect?.value;
    filter.searchCompany = this.searchCompanyAutocomplete?.value?.id;

    return filter;
  }

  /** ACTIONS */
  /**
   * Delete role
   *
   * @param _item
   */
  deleteRole(_item: Role) {
    const _title: string = this.translate.instant('ROLES.DELETE_ROLE_SIMPLE.TITLE');
    const _description: string = this.translate.instant('ROLES.DELETE_ROLE_SIMPLE.DESCRIPTION');
    const _waitDesciption: string = this.translate.instant(
      'ROLES.DELETE_ROLE_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new RoleOnServerDeleted({ id: _item.id }));
    });
  }

  /**
   * Delete roles
   *
   */
  deleteRoles() {
    const _title: string = this.translate.instant('ROLES.DELETE_ROLE_MULTI.TITLE');
    const _description: string = this.translate.instant('ROLES.DELETE_ROLE_MULTI.DESCRIPTION');
    const _waitDesciption: string = this.translate.instant(
      'ROLES.DELETE_ROLE_MULTI.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const ids: number[] = [];
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < this.selection.selected.length; i++) {
        ids.push(this.selection.selected[i].id);
      }
      this.store.dispatch(new RolesOnServerDeleted({ ids }));
    });
  }

  /** Fetch */
  /**
   * Fetch selected rows
   */
  fetchRoles() {
    const messages = [];
    this.selection.selected.forEach((elem) => {
      messages.push({
        text: `${elem.title}`,
        id: elem.id.toString(),
        // status: elem.username
      });
    });
    this.layoutUtilsService.fetchElements(messages);
  }

  /**
   * Add role
   */
  addRole() {
    const newRole = new Role();
    newRole.clear(); // Set all defaults fields
    this.editRole(newRole);
  }

  /**
   * Edit role
   *
   * @param role
   */
  editRole(role: Role) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.maxHeight = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.width = '100vw';
    this.dialog.open(RoleEditDialogComponent, {
      data: { roleId: role.id, roleTitle: role.title, companies: this.companiesNoAdminRole },
    });
  }

  /**
   * Check all rows are selected
   */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.rolesResult.length;
    return numSelected === numRows;
  }

  /**
   * Toggle selection
   */
  masterToggle() {
    if (this.selection.selected.length === this.rolesResult.length) {
      this.selection.clear();
    } else {
      this.rolesResult.forEach((row) => this.selection.select(row));
    }
  }

  getCompanyName(companyID) {
    // tslint:disable-next-line:max-line-length
    return this.companies.find((company) => company.id === companyID) !== undefined
      ? this.companies.find((company) => company.id === companyID).name
      : '';
  }

  formatCompanyOption(company): string {
    return company?.name || '';
  }

  reloadRolesList(): void {
    this.paginator.pageIndex = 0;

    this.loadRolesList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchTitleInput.nativeElement.value = '';
    this.searchCompanyAutocomplete?.setValue(null);
    this.searchCompanyAutocomplete?.updateValueAndValidity();

    if (!this.isSuperAdmin) this.searchDepartmentSelect.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadRolesList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchTitleInput.nativeElement.focus());
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadRolesList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadRolesList();
  }

  exportRolesAsCSV(event: Event): void {
    event.stopPropagation();

    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.authService
      .getRolesAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        const filename = `Roles List [${utc(new Date())
          .local()
          .format('YYYY-MM-DD, hh.mm a')}].csv`;
        this.downloadService.downloadFile(res, filename, 'text/csv');
      });
  }
}
